
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import Quill from 'quill/dist/quill.js'
import 'quill/dist/quill.bubble.css'

type QuillOptions = ConstructorParameters<typeof Quill>[1]
const Parchment = Quill.import('parchment')

// === Регистрация кастомных атрибутов ===
const registerCustomAttribute = (name: string, key: string, scope: any) => {
  const attr = new Parchment.Attributor(key, name, { scope })
  Quill.register({ [`attributors/attribute/${name}`]: attr }, true)
  Quill.register({ [`formats/${name}`]: attr }, true)
}

registerCustomAttribute('id', 'id-attribute', Parchment.Scope.BLOCK)
registerCustomAttribute('data-seo-item-id', 'data-seo-item-id', Parchment.Scope.INLINE)
registerCustomAttribute('data-component-id', 'data-component-id', Parchment.Scope.INLINE)
registerCustomAttribute('data-autolink', 'data-autolink', Parchment.Scope.INLINE)

// === Расширение Link для поддержки data-* атрибутов ===
const Link = Quill.import('formats/link')
Link.SANITIZING = false // Отключаем автоматическую очистку href / rel / target

Link.formats = function (domNode: HTMLElement) {
  const formats: Record<string, any> = {}

  const href = domNode.getAttribute('href')
  if (href) formats.href = href

  const rel = domNode.getAttribute('rel')
  if (rel) formats.rel = rel

  const target = domNode.getAttribute('target')
  if (target) formats.target = target

  ;['data-seo-item-id', 'data-component-id', 'data-autolink'].forEach(attr => {
    const val = domNode.getAttribute(attr)
    if (val !== null) formats[attr] = val
  })

  return formats
}

Link.create = function (value: string | Record<string, any>) {
  const node = document.createElement('a')

  if (typeof value === 'string') {
    node.setAttribute('href', value)
  } else if (value && typeof value === 'object' && 'href' in value) {
    node.setAttribute('href', value.href || '#')

    if ('target' in value) node.setAttribute('target', value.target)
    if ('rel' in value) node.setAttribute('rel', value.rel)

    ;['data-seo-item-id', 'data-component-id', 'data-autolink'].forEach(attr => {
      if (attr in value) node.setAttribute(attr, value[attr])
    })
  }

  node.setAttribute('dir', 'auto')
  node.textContent = node.textContent || ''
  return node
}

Quill.register(Link, true)

@Component
export default class HtmlRichEditor extends Vue {
  @Prop({ type: String, default: '' }) readonly value!: string

  private quill: Quill | null = null
  private ignoreNextEmit = false

  private readonly defaultOptions: QuillOptions = {
    theme: 'snow',
    modules: {
      toolbar: [
        // Стиль текста
        [{ font: [] }, { size: [] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        // Структура блока
        [{ header: [1, 2, 3, 4, 5, 6] }],
        ['blockquote', 'code-block'],
        // Списки и отступы
        [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
        [{ indent: '-1' }, { indent: '+1' }],
        // Выравнивание и направление
        [{ align: [] }, { direction: 'rtl' }],
        // Вставки
        ['link', 'image', 'video', 'formula'],
        // Очистка форматирования
        ['clean']
      ]
    }
  };

  mounted() {
    this.quill = new Quill(this.$refs.editor as HTMLElement, this.defaultOptions)

    if (this.value) {
      this.setQuillContent(this.value)
    }

    this.quill.on('text-change', () => {
      if (this.ignoreNextEmit) {
        this.ignoreNextEmit = false
        return
      }

      const editor = this.$refs.editor as HTMLElement
      const html = editor.querySelector('.ql-editor')?.innerHTML || ''

      this.$emit('input', html)
    })
  }

  @Watch('value')
  onValueChanged(newVal: string) {
    if (!this.quill) return

    const editor = this.$refs.editor as HTMLElement
    const qlEditor = editor.querySelector('.ql-editor')!

    if (newVal !== qlEditor.innerHTML) {
      const selection = this.quill.getSelection()
      this.setQuillContent(newVal)
      if (selection) this.quill.setSelection(selection)
    }
  }

  beforeDestroy() {
    if (this.quill) {
      this.quill.off('text-change')
      this.quill = null
    }
  }

  // Вспомогательная функция: безопасное обновление контента
  private setQuillContent(html: string) {
    this.ignoreNextEmit = true
    this.quill?.clipboard.dangerouslyPasteHTML(html)
  }
}
